import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CategoriesBlock.module.scss';
import { categoryIcons } from '../../assets/icons/categoryIcons';
import { ReactComponent as Close } from '../../assets/icons/Close_MD.svg';

// Dropdown
function Dropdown({ value, options, onChange, placeholder = 'Иконка' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  return (
    <div ref={ref} className={styles.dd}>
      <button
        type='button'
        className={styles.dd__btn}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.dd__btnText}>
          {selected ? (
            <span className={styles.dd__label}>
              <span className={styles.dd__labelIcon}>{selected.icon}</span>
            </span>
          ) : (
            placeholder
          )}
        </span>
      </button>

      <div
        className={[
          styles.dd__menu,
          open ? styles.dd__menuOpen : styles.dd__menuClosed,
        ].join(' ')}
        aria-hidden={!open}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type='button'
            className={o.value === value ? styles.dd__itemActive : styles.dd__item}
            tabIndex={open ? 0 : -1}
            onClick={() => {
              onChange(o.value);
              setOpen(false);
            }}
          >
            <span className={styles.dd__itemIcon}>{o.icon}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const kindTitle = (kind) => (kind === 'income' ? 'income' : 'expense');

const CategoriesBlock = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [expenseName, setExpenseName] = useState('');
  const [incomeName, setIncomeName] = useState('');

  const [expenseIcon, setExpenseIcon] = useState('Star');
  const [incomeIcon, setIncomeIcon] = useState('Star');

  const [saving, setSaving] = useState({ expense: false, income: false });
  const [deletingId, setDeletingId] = useState(null);

  const iconOptions = useMemo(() => {
    const entries = Object.entries(categoryIcons || {});
    return entries.map(([key, Comp]) => ({
      value: key,
      label: key,
      icon: <Comp />,
    }));
  }, []);

  const deleteCategory = async (c) => {
    if (!c?.id) return;
    if (deletingId) return;

    const ok = window.confirm(`Удалить категорию "${c.name}" (${c.kind})?`);
    if (!ok) return;

    setDeletingId(c.id);

    try {
      const res = await fetch(`/api/categories/${c.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка удаления');

      setItems((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      alert(e.message || 'Ошибка');
    } finally {
      setDeletingId(null);
    }
  };

  const load = async (signal) => {
    const res = await fetch('/api/categories', { signal });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки категорий');
    return Array.isArray(data) ? data : [];
  };

  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const rows = await load(ctrl.signal);
        setItems(rows);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, []);

  const expense = useMemo(
    () => items.filter((c) => c.kind === 'expense'),
    [items]
  );
  const income = useMemo(
    () => items.filter((c) => c.kind === 'income'),
    [items]
  );

  const createCategory = async (kind) => {
    const name = (kind === 'expense' ? expenseName : incomeName).trim();
    const icon = kind === 'expense' ? expenseIcon : incomeIcon;

    if (!name) return;

    setSaving((s) => ({ ...s, [kind]: true }));

    try {
      const res = await fetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, kind, icon }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка создания категории');

      setItems((prev) => {
        const next = [data, ...prev];
        next.sort((a, b) => {
          if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
          return String(a.name).localeCompare(String(b.name), 'ru');
        });
        return next;
      });

      if (kind === 'expense') {
        setExpenseName('');
        setExpenseIcon('Star');
      } else {
        setIncomeName('');
        setIncomeIcon('Star');
      }
    } catch (e) {
      alert(e.message || 'Ошибка');
    } finally {
      setSaving((s) => ({ ...s, [kind]: false }));
    }
  };

  const onSubmit = (e, kind) => {
    e.preventDefault();
    if (saving[kind]) return;
    createCategory(kind);
  };

  const renderList = (rows) =>
    rows.map((c) => {
      const Icon = categoryIcons?.[c.icon] || categoryIcons?.Star || null;

      return (
        <div key={c.id} className={styles.categories__row}>
          <div className={styles.categories__iconBox}>
            {Icon ? <Icon className={styles.categories__icon} /> : null}
          </div>

          <div className={styles.categories__colName}>{c.name}</div>

          <div className={styles.categories__colKind}>
            <span
              className={`${styles.categories__kind} ${
                c.kind === 'income'
                  ? styles.categories__kind_income
                  : styles.categories__kind_expense
              }`}
            >
              {kindTitle(c.kind)}
            </span>
          </div>

          <button
            className={styles.categories__delete}
            disabled={deletingId === c.id}
            onClick={() => deleteCategory(c)}
          >
            <Close className={styles.categories__deleteIcon} />
          </button>
        </div>
      );
    });

  if (loading) {
    return <div className={styles.categories__block}>Загрузка...</div>;
  }

  return (
    <div className={styles.categories__block}>
      <div className={styles.categories__head}>
        <h3 className={styles.categories__title}>Категории</h3>
        <span className={styles.categories__count}>{items.length}</span>
      </div>

      <div className={styles.categories__sections}>
        <div className={styles.categories__section}>
          <div className={styles.categories__sectionHead}>
            <span className={styles.categories__sectionTitle_expense}>
              Расходы
            </span>
            <span className={styles.categories__sectionCount}>
              {expense.length}
            </span>
          </div>

          <h3 className={styles.add__title}>Новая категория расходов</h3>
          <form
            className={styles.categories__add}
            onSubmit={(e) => onSubmit(e, 'expense')}
          >
            <Dropdown
              value={expenseIcon}
              options={iconOptions}
              onChange={setExpenseIcon}
              placeholder='Иконка'
            />

            <input
              type='text'
              placeholder='Название'
              className={styles.add__input}
              value={expenseName}
              onChange={(e) => setExpenseName(e.target.value)}
            />

            <button
              className={styles.add__button}
              type='submit'
              disabled={saving.expense || !expenseName.trim()}
            >
              <Close className={styles.add__icon} />
            </button>
          </form>

          <div className={styles.categories__table}>{renderList(expense)}</div>
        </div>

        <div className={styles.categories__section}>
          <div className={styles.categories__sectionHead}>
            <span className={styles.categories__sectionTitle_income}>
              Доходы
            </span>
            <span className={styles.categories__sectionCount}>{income.length}</span>
          </div>

          <h3 className={styles.add__title}>Новая категория доходов</h3>
          <form
            className={styles.categories__add}
            onSubmit={(e) => onSubmit(e, 'income')}
          >
            <Dropdown
              value={incomeIcon}
              options={iconOptions}
              onChange={setIncomeIcon}
              placeholder='Иконка'
            />

            <input
              type='text'
              placeholder='Название'
              className={styles.add__input}
              value={incomeName}
              onChange={(e) => setIncomeName(e.target.value)}
            />

            <button
              className={styles.add__button}
              type='submit'
              disabled={saving.income || !incomeName.trim()}
            >
              <Close className={styles.add__icon} />
            </button>
          </form>

          <div className={styles.categories__table}>{renderList(income)}</div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesBlock;
